import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UploadSimple, Trash, ImageSquare, Tag, LinkSimple } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import { Input, Textarea, Checkbox, Button, SeoFields } from "../../components/ui";

const empty = { image: null, image_alt: "", name: "", description: "", slug: "", featured: false, meta_title: "", meta_description: "" };

const BrandModal = ({ isOpen, onClose, setBrandToUpdate, BrandToUpdate, setDone, done }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(empty);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => (document.body.style.overflow = "unset");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && BrandToUpdate?.id) {
      setFormData({
        image: BrandToUpdate.image || null,
        image_alt: BrandToUpdate.image_alt || "",
        name: BrandToUpdate.name || "",
        description: BrandToUpdate.description || "",
        slug: BrandToUpdate.slug || "",
        featured: BrandToUpdate.featured || false,
        meta_title: BrandToUpdate.meta_title || "",
        meta_description: BrandToUpdate.meta_description || "",
      });
    } else {
      setFormData(empty);
    }
  }, [isOpen, BrandToUpdate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = BrandToUpdate?.id
        ? await axiosInstance.put(`/brands/update/${BrandToUpdate.id}`, formData)
        : await axiosInstance.post("/brands/create", formData);
      if (response.data) {
        toast.success(response.data.message);
        CloseModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.warning("File size exceeds 15 MB limit");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setFormData((prev) => ({ ...prev, image: reader.result }));
  };

  const CloseModal = () => {
    setFormData(empty);
    setBrandToUpdate({});
    setDone(!done);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={CloseModal}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-pop"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">
                  {BrandToUpdate?.id ? "Edit brand" : "New brand"}
                </h2>
                <p className="text-xs text-muted">Brand details and visibility.</p>
              </div>
              <button onClick={CloseModal} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <form id="brand-form" onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
              {/* Logo uploader */}
              <div>
                <label className="mb-2 block text-sm font-medium text-ink/90">Logo</label>
                <div className="flex items-center gap-4">
                  <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl border border-line bg-surface-2">
                    {formData.image ? (
                      <img src={formData.image} alt="Brand" className="h-full w-full object-cover" />
                    ) : (
                      <ImageSquare size={24} className="text-faint" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink transition hover:bg-surface-2">
                        <UploadSimple size={16} /> Upload
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                      </label>
                      {formData.image && (
                        <button
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, image: null }))}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-red-600 transition hover:bg-red-500/10"
                        >
                          <Trash size={16} /> Remove
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted">JPG, PNG or GIF · max 15MB</p>
                  </div>
                </div>
                {formData.image && (
                  <Input
                    containerClassName="mt-3"
                    label="Logo alt text"
                    value={formData.image_alt}
                    onChange={(e) => setFormData((p) => ({ ...p, image_alt: e.target.value }))}
                    placeholder="Describe the logo for SEO & accessibility"
                  />
                )}
              </div>

              <Input label="Name" icon={Tag} name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Bosch" required />
              <Input label="Slug" icon={LinkSimple} name="slug" value={formData.slug} onChange={handleChange} placeholder="e.g. bosch" required />
              <Textarea label="Description" name="description" value={formData.description} onChange={handleChange} placeholder="Short description of the brand…" />

              <Checkbox
                checked={!!formData.featured}
                onChange={(val) => setFormData((p) => ({ ...p, featured: val }))}
                label="Feature this brand on the homepage"
                description="When checked, this brand appears on the homepage for better visibility."
              />

              <SeoFields
                value={{ slug: formData.slug, meta_title: formData.meta_title, meta_description: formData.meta_description }}
                onChange={(field, val) => setFormData((p) => ({ ...p, [field]: val }))}
                fallbackTitle={formData.name}
                fallbackDescription={formData.description}
                basePath="/brand/"
                showSlug={false}
              />
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={CloseModal}>
                Cancel
              </Button>
              <Button type="submit" form="brand-form" loading={loading}>
                {BrandToUpdate?.id ? "Save changes" : "Create brand"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrandModal;
